import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeft, Type, Square, Circle, Save,
  Download, Trash2, Move, Plus, Minus, Layers,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  MapPin, Clock, Gift, Shirt, Phone, Heart, Users, Music, Utensils, Camera, MapPinned
} from "lucide-react";

const CANVAS_W = 600;
const CANVAS_H = 900;

// === QUICK-INSERT INFO BLOCKS ===
const INFO_BLOCKS = [
  { icon: MapPin, label: "Ubicación", defaultText: "Hacienda Los Arcos\nCalle 60 #456, Col. Centro\nMérida, Yucatán", fontSize: 16 },
  { icon: MapPinned, label: "Link Google Maps", defaultText: "Ver ubicación en Google Maps\nmaps.google.com/?q=...", fontSize: 14 },
  { icon: Clock, label: "Fecha y Hora", defaultText: "Sábado 15 de Junio, 2026\n18:00 hrs", fontSize: 20 },
  { icon: Utensils, label: "Recepción", defaultText: "Recepción\n20:00 hrs\nSalón Principal", fontSize: 16 },
  { icon: Gift, label: "Mesa de Regalos", defaultText: "Mesa de Regalos\nLiverpool No. 12345678\nAmazon: amazon.com.mx/...", fontSize: 14 },
  { icon: Shirt, label: "Código de Vestimenta", defaultText: "Código de Vestimenta\nFormal / Cocktail\nColor: Libre", fontSize: 15 },
  { icon: Phone, label: "RSVP / Confirmar", defaultText: "Confirma tu asistencia\nWhatsApp: 999 338 8933\nAntes del 1 de Junio", fontSize: 14 },
  { icon: Heart, label: "Padres / Padrinos", defaultText: "Con la bendición de nuestros padres\nSr. y Sra. García López\nSr. y Sra. Martínez Ruiz", fontSize: 14 },
  { icon: Users, label: "Padrinos", defaultText: "Padrinos de Honor\nJuan y María Pérez", fontSize: 15 },
  { icon: Music, label: "Programa / Itinerario", defaultText: "Programa\n18:00 Ceremonia Religiosa\n19:00 Sesión de Fotos\n20:00 Recepción\n21:00 Vals / Brindis\n22:00 Fiesta", fontSize: 13 },
  { icon: Camera, label: "Hashtag / Fotos", defaultText: "#MariayJuan2026\nComparte tus fotos con nuestro hashtag", fontSize: 15 },
];

// === TEMPLATES ===
const TEMPLATES = [
  // BODAS
  {
    id: "boda_elegante", nombre: "Boda Elegante", tipo: "Boda", bgColor: "#1a1a2e",
    elements: [
      { id: "t1", type: "text", x: 300, y: 80, text: "Nos Casamos", fontFamily: "Playfair Display", fontSize: 48, color: "#d4af37", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 170, text: "María & Juan", fontFamily: "Great Vibes", fontSize: 52, color: "#ffffff", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "d1", type: "line", x: 150, y: 230, width: 300, color: "#d4af37" },
      { id: "t3", type: "text", x: 300, y: 270, text: "Con la bendición de nuestros padres\nSr. y Sra. García López\nSr. y Sra. Martínez Ruiz", fontFamily: "Montserrat", fontSize: 13, color: "#aaaaaa", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t4", type: "text", x: 300, y: 380, text: "Sábado 15 de Junio, 2026\n18:00 hrs", fontFamily: "Montserrat", fontSize: 20, color: "#d4af37", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t5", type: "text", x: 300, y: 470, text: "Ceremonia Religiosa\nParroquia de Guadalupe\nCalle 60 #234, Centro", fontFamily: "Montserrat", fontSize: 14, color: "#cccccc", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "d2", type: "line", x: 200, y: 540, width: 200, color: "#d4af37" },
      { id: "t6", type: "text", x: 300, y: 570, text: "Recepción\nHacienda Los Arcos\nCarretera Mérida-Progreso Km 12", fontFamily: "Montserrat", fontSize: 14, color: "#cccccc", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 670, text: "Código de Vestimenta: Formal\nMesa de Regalos: Liverpool #12345", fontFamily: "Montserrat", fontSize: 12, color: "#888888", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t8", type: "text", x: 300, y: 770, text: "Confirma tu asistencia\nWhatsApp: 999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#d4af37", bold: false, italic: false, underline: false, align: "center", width: 400 },
      { id: "t9", type: "text", x: 300, y: 850, text: "#MaríayJuan2026", fontFamily: "Montserrat", fontSize: 12, color: "#666666", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  {
    id: "boda_rustica", nombre: "Boda Rústica", tipo: "Boda", bgColor: "#faf7f2",
    elements: [
      { id: "t1", type: "text", x: 300, y: 70, text: "Juntos para siempre", fontFamily: "Dancing Script", fontSize: 28, color: "#8b7355", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 140, text: "Ana & Carlos", fontFamily: "Playfair Display", fontSize: 52, color: "#5c4a32", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "d1", type: "rect", x: 200, y: 195, width: 200, height: 2, color: "#c9b896" },
      { id: "t3", type: "text", x: 300, y: 240, text: "Nuestros padres\nSr. Roberto y Sra. Elena\nSr. Miguel y Sra. Laura", fontFamily: "Cormorant Garamond", fontSize: 14, color: "#7a6b55", bold: false, italic: true, underline: false, align: "center", width: 460 },
      { id: "t4", type: "text", x: 300, y: 350, text: "12 de Octubre, 2026\n17:00 horas", fontFamily: "Playfair Display", fontSize: 24, color: "#5c4a32", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t5", type: "text", x: 300, y: 440, text: "Ceremonia\nCapilla del Rancho\nKm 8 Carretera a Uxmal", fontFamily: "Montserrat", fontSize: 14, color: "#8b7355", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t6", type: "text", x: 300, y: 540, text: "Recepción\nRancho San José\nDress code: Semi-formal", fontFamily: "Montserrat", fontSize: 14, color: "#8b7355", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 650, text: "Mesa de Regalos\nAmazon: bit.ly/AnaCarlos\nLiverpool: 987654321", fontFamily: "Montserrat", fontSize: 13, color: "#a0917b", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t8", type: "text", x: 300, y: 760, text: "RSVP antes del 20 de Septiembre\n999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#5c4a32", bold: true, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  {
    id: "boda_minimalista", nombre: "Boda Minimalista", tipo: "Boda", bgColor: "#ffffff",
    elements: [
      { id: "t1", type: "text", x: 300, y: 120, text: "L  &  P", fontFamily: "Cinzel", fontSize: 64, color: "#1a1a1a", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "d1", type: "line", x: 250, y: 185, width: 100, color: "#1a1a1a" },
      { id: "t2", type: "text", x: 300, y: 230, text: "Lucía Hernández\n&\nPablo Sánchez", fontFamily: "Cormorant Garamond", fontSize: 22, color: "#333333", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t3", type: "text", x: 300, y: 380, text: "08 . 11 . 2026", fontFamily: "Montserrat", fontSize: 28, color: "#1a1a1a", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t4", type: "text", x: 300, y: 430, text: "seis de la tarde", fontFamily: "Cormorant Garamond", fontSize: 18, color: "#666666", bold: false, italic: true, underline: false, align: "center", width: 500 },
      { id: "t5", type: "text", x: 300, y: 510, text: "Jardín Borda\nMorelos #271, Cuernavaca", fontFamily: "Montserrat", fontSize: 15, color: "#333333", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t6", type: "text", x: 300, y: 620, text: "Dress code: Black tie\nMesa de regalos: Amazon", fontFamily: "Montserrat", fontSize: 13, color: "#888888", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 740, text: "RSVP\n999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#1a1a1a", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  // XV AÑOS
  {
    id: "xv_rosa", nombre: "XV Años Rosa", tipo: "XV Años", bgColor: "#fdf2f8",
    elements: [
      { id: "t1", type: "text", x: 300, y: 70, text: "Mis XV Años", fontFamily: "Great Vibes", fontSize: 52, color: "#be185d", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 160, text: "Sofía", fontFamily: "Playfair Display", fontSize: 64, color: "#9d174d", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t3", type: "text", x: 300, y: 250, text: "Te invito a celebrar conmigo\neste día tan especial", fontFamily: "Montserrat", fontSize: 16, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t4", type: "text", x: 300, y: 350, text: "Mis padres\nJosé y Carmen Rodríguez\nY mis padrinos\nLuis y Rosa Méndez", fontFamily: "Montserrat", fontSize: 13, color: "#9ca3af", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t5", type: "text", x: 300, y: 470, text: "Sábado 20 de Marzo\n17:00 hrs", fontFamily: "Playfair Display", fontSize: 24, color: "#be185d", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t6", type: "text", x: 300, y: 550, text: "Misa de Acción de Gracias\nIglesia de San Cristóbal\n17:00 hrs", fontFamily: "Montserrat", fontSize: 14, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 650, text: "Recepción\nSalón Imperial\nAv. Colón #789, Mérida\n19:00 hrs", fontFamily: "Montserrat", fontSize: 14, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t8", type: "text", x: 300, y: 770, text: "Dress code: Formal / Rosa pastel\nMesa de regalos: Liverpool #456789", fontFamily: "Montserrat", fontSize: 12, color: "#9ca3af", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t9", type: "text", x: 300, y: 850, text: "RSVP: 999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#be185d", bold: true, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  {
    id: "xv_dorado", nombre: "XV Años Dorado", tipo: "XV Años", bgColor: "#1c1917",
    elements: [
      { id: "t1", type: "text", x: 300, y: 80, text: "XV", fontFamily: "Cinzel", fontSize: 72, color: "#d4af37", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 170, text: "Valentina", fontFamily: "Great Vibes", fontSize: 56, color: "#f5e6c8", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "d1", type: "line", x: 150, y: 220, width: 300, color: "#d4af37" },
      { id: "t3", type: "text", x: 300, y: 270, text: "Mis padres Mario y Claudia\ntienen el honor de invitarte", fontFamily: "Cormorant Garamond", fontSize: 15, color: "#a8a29e", bold: false, italic: true, underline: false, align: "center", width: 460 },
      { id: "t4", type: "text", x: 300, y: 370, text: "25 de Julio, 2026\n19:00 hrs", fontFamily: "Montserrat", fontSize: 22, color: "#d4af37", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t5", type: "text", x: 300, y: 460, text: "Misa\nCatedral de Mérida\nCalle 60 x 61, Centro", fontFamily: "Montserrat", fontSize: 14, color: "#d6d3d1", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t6", type: "text", x: 300, y: 560, text: "Recepción\nHotel Fiesta Americana\nSalón Gran Marqués", fontFamily: "Montserrat", fontSize: 14, color: "#d6d3d1", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 670, text: "Programa\n19:00 Misa  |  20:30 Recepción\n21:00 Vals  |  22:00 Fiesta", fontFamily: "Montserrat", fontSize: 12, color: "#a8a29e", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t8", type: "text", x: 300, y: 780, text: "Dress code: Gala / Negro y dorado", fontFamily: "Montserrat", fontSize: 13, color: "#d4af37", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t9", type: "text", x: 300, y: 850, text: "RSVP: 999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#f5e6c8", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  // CUMPLEAÑOS
  {
    id: "cumple_moderno", nombre: "Cumpleaños Moderno", tipo: "Cumpleaños", bgColor: "#0f172a",
    elements: [
      { id: "t1", type: "text", x: 300, y: 100, text: "LET'S PARTY!", fontFamily: "Montserrat", fontSize: 48, color: "#f59e0b", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 200, text: "Carlos cumple 30", fontFamily: "Playfair Display", fontSize: 36, color: "#ffffff", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "d1", type: "line", x: 200, y: 250, width: 200, color: "#f59e0b" },
      { id: "t3", type: "text", x: 300, y: 310, text: "Viernes 10 de Abril\n21:00 hrs", fontFamily: "Montserrat", fontSize: 22, color: "#f59e0b", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t4", type: "text", x: 300, y: 420, text: "Bar El Rincón\nCalle 60 #456\nColonia Centro, Mérida", fontFamily: "Montserrat", fontSize: 16, color: "#94a3b8", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t5", type: "text", x: 300, y: 540, text: "Dress code: Casual elegante\nHabrá barra libre toda la noche", fontFamily: "Montserrat", fontSize: 14, color: "#64748b", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t6", type: "text", x: 300, y: 680, text: "Confirma: 999 338 8933\n#Carlos30", fontFamily: "Montserrat", fontSize: 14, color: "#f59e0b", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  {
    id: "cumple_floral", nombre: "Cumpleaños Floral", tipo: "Cumpleaños", bgColor: "#fefce8",
    elements: [
      { id: "t1", type: "text", x: 300, y: 80, text: "Estás Invitado", fontFamily: "Dancing Script", fontSize: 36, color: "#a16207", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 170, text: "Elena cumple 50", fontFamily: "Playfair Display", fontSize: 40, color: "#713f12", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t3", type: "text", x: 300, y: 290, text: "Sábado 5 de Mayo\n14:00 hrs", fontFamily: "Montserrat", fontSize: 20, color: "#a16207", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t4", type: "text", x: 300, y: 400, text: "Jardín La Quinta\nCalle 25 x 30, García Ginerés\nMérida, Yucatán", fontFamily: "Montserrat", fontSize: 15, color: "#78716c", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t5", type: "text", x: 300, y: 530, text: "Habrá comida, música y mucha diversión", fontFamily: "Cormorant Garamond", fontSize: 18, color: "#92400e", bold: false, italic: true, underline: false, align: "center", width: 460 },
      { id: "t6", type: "text", x: 300, y: 680, text: "Confirma tu asistencia\n999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#a16207", bold: true, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  // BAUTIZO
  {
    id: "bautizo_clasico", nombre: "Bautizo Clásico", tipo: "Bautizo", bgColor: "#f0f9ff",
    elements: [
      { id: "t1", type: "text", x: 300, y: 80, text: "Mi Bautizo", fontFamily: "Playfair Display", fontSize: 48, color: "#1e40af", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 170, text: "Mateo Alexander", fontFamily: "Great Vibes", fontSize: 44, color: "#1e3a5f", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t3", type: "text", x: 300, y: 250, text: "Con la bendición de Dios\nY el amor de mis padres\nDaniel y Gabriela", fontFamily: "Montserrat", fontSize: 14, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t4", type: "text", x: 300, y: 360, text: "Mis Padrinos\nJorge y Patricia Domínguez", fontFamily: "Cormorant Garamond", fontSize: 16, color: "#3b82f6", bold: false, italic: true, underline: false, align: "center", width: 460 },
      { id: "t5", type: "text", x: 300, y: 460, text: "Domingo 5 de Mayo, 2026\n12:00 hrs", fontFamily: "Montserrat", fontSize: 20, color: "#1e40af", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t6", type: "text", x: 300, y: 550, text: "Parroquia de San Miguel Arcángel\nCalle 50 x 27, Centro\nMérida, Yucatán", fontFamily: "Montserrat", fontSize: 14, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 670, text: "Recepción\nJardín Casa Grande\nCalle 23 #456", fontFamily: "Montserrat", fontSize: 14, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t8", type: "text", x: 300, y: 800, text: "RSVP: 999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#1e40af", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  // BABY SHOWER
  {
    id: "baby_shower", nombre: "Baby Shower", tipo: "Baby Shower", bgColor: "#f0fdf4",
    elements: [
      { id: "t1", type: "text", x: 300, y: 80, text: "Baby Shower", fontFamily: "Dancing Script", fontSize: 48, color: "#16a34a", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 180, text: "Viene en camino", fontFamily: "Montserrat", fontSize: 18, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t3", type: "text", x: 300, y: 240, text: "Emiliano", fontFamily: "Playfair Display", fontSize: 52, color: "#166534", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t4", type: "text", x: 300, y: 350, text: "Acompaña a Laura en esta\ncelebración llena de amor", fontFamily: "Cormorant Garamond", fontSize: 18, color: "#4b5563", bold: false, italic: true, underline: false, align: "center", width: 460 },
      { id: "t5", type: "text", x: 300, y: 460, text: "Sábado 15 de Febrero\n11:00 hrs", fontFamily: "Montserrat", fontSize: 22, color: "#16a34a", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t6", type: "text", x: 300, y: 560, text: "Casa de Laura\nCalle 35 #678\nFraccionamiento Las Américas", fontFamily: "Montserrat", fontSize: 14, color: "#6b7280", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 700, text: "Mesa de regalos: Amazon Baby\nbit.ly/BabyEmiliano", fontFamily: "Montserrat", fontSize: 13, color: "#9ca3af", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t8", type: "text", x: 300, y: 800, text: "Confirma: 999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#16a34a", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  // PRIMERA COMUNIÓN
  {
    id: "comunion", nombre: "Primera Comunión", tipo: "Comunión", bgColor: "#fffbeb",
    elements: [
      { id: "t1", type: "text", x: 300, y: 80, text: "Primera Comunión", fontFamily: "Playfair Display", fontSize: 40, color: "#92400e", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t2", type: "text", x: 300, y: 170, text: "Isabella", fontFamily: "Great Vibes", fontSize: 56, color: "#78350f", bold: false, italic: false, underline: false, align: "center", width: 500 },
      { id: "t3", type: "text", x: 300, y: 260, text: "Con la bendición de Dios\nrecibe por primera vez\nel Cuerpo de Cristo", fontFamily: "Cormorant Garamond", fontSize: 16, color: "#78716c", bold: false, italic: true, underline: false, align: "center", width: 460 },
      { id: "t4", type: "text", x: 300, y: 390, text: "Domingo 18 de Mayo\n10:00 hrs", fontFamily: "Montserrat", fontSize: 22, color: "#92400e", bold: true, italic: false, underline: false, align: "center", width: 500 },
      { id: "t5", type: "text", x: 300, y: 480, text: "Iglesia del Sagrado Corazón\nCalle 47 x 60, Centro", fontFamily: "Montserrat", fontSize: 15, color: "#78716c", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t6", type: "text", x: 300, y: 580, text: "Recepción\nSalón Jardín\nCalle 30 #123", fontFamily: "Montserrat", fontSize: 14, color: "#78716c", bold: false, italic: false, underline: false, align: "center", width: 460 },
      { id: "t7", type: "text", x: 300, y: 720, text: "Confirma: 999 338 8933", fontFamily: "Montserrat", fontSize: 14, color: "#92400e", bold: false, italic: false, underline: false, align: "center", width: 400 },
    ]
  },
  // BLANCO
  {
    id: "blanco", nombre: "Lienzo en Blanco", tipo: "Otro", bgColor: "#ffffff",
    elements: []
  }
];

const FONTS = [
  "Playfair Display", "Montserrat", "Lato", "Open Sans", "Roboto",
  "Dancing Script", "Great Vibes", "Cinzel", "Cormorant Garamond", "Josefin Sans"
];

const InvitacionDesigner = () => {
  const { invitacionId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [invitacion, setInvitacion] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [elements, setElements] = useState([]);
  const [bgColor, setBgColor] = useState("#ffffff");
  const [selectedId, setSelectedId] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [editingText, setEditingText] = useState(null);
  const [templateFilter, setTemplateFilter] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const invRes = await axios.get(`${API}/invitaciones/${invitacionId}`);
        setInvitacion(invRes.data);
        if (invRes.data.cliente_id) {
          try {
            const clienteRes = await axios.get(`${API}/clientes/${invRes.data.cliente_id}`);
            setCliente(clienteRes.data);
          } catch {
            // Client may have been deleted - non-blocking
            toast.error("Cliente no encontrado");
          }
        }
        if (invRes.data.diseno_data) {
          setElements(invRes.data.diseno_data.elements || []);
          setBgColor(invRes.data.diseno_data.bgColor || "#ffffff");
          setShowTemplates(false);
        }
      } catch (error) {
        setLoadError("No se pudo cargar la invitación. Verifica que exista.");
        toast.error("Error al cargar la invitación");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [invitacionId]);

  useEffect(() => {
    const id = "eventops-google-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Montserrat:wght@300;400;600;700&family=Dancing+Script&family=Great+Vibes&family=Cinzel&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Josefin+Sans:wght@300;400;600&family=Lato:wght@300;400;700&family=Open+Sans:wght@300;400;600&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
  }, []);

  const selectedElement = elements.find(el => el.id === selectedId);

  const applyTemplate = (template) => {
    setElements(template.elements.map(el => ({ ...el, id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` })));
    setBgColor(template.bgColor);
    setShowTemplates(false);
    setSelectedId(null);
  };

  const addText = () => {
    const newEl = {
      id: `el_${Date.now()}`, type: "text", x: CANVAS_W / 2, y: 400 + Math.random() * 100,
      text: "Nuevo texto", fontFamily: "Montserrat", fontSize: 24,
      color: bgColor === "#ffffff" ? "#1a1a1a" : "#ffffff",
      bold: false, italic: false, underline: false, align: "center", width: 400
    };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };

  const addInfoBlock = (block) => {
    const newEl = {
      id: `el_${Date.now()}`, type: "text",
      x: CANVAS_W / 2, y: 400 + Math.random() * 100,
      text: block.defaultText, fontFamily: "Montserrat", fontSize: block.fontSize,
      color: bgColor === "#ffffff" ? "#555555" : "#cccccc",
      bold: false, italic: false, underline: false, align: "center", width: 460
    };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };

  const addShape = (type) => {
    const newEl = {
      id: `el_${Date.now()}`, type, x: CANVAS_W / 2 - 50, y: 400,
      width: type === "line" ? 200 : 100, height: type === "line" ? 2 : 100,
      color: "#d4af37", borderRadius: type === "circle" ? 50 : 0
    };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };

  const updateElement = useCallback((id, updates) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
  }, []);

  const deleteElement = (id) => {
    setElements(prev => prev.filter(el => el.id !== id));
    setSelectedId(null);
  };

  const handleMouseDown = (e, el) => {
    e.stopPropagation();
    if (editingText === el.id) return;
    setSelectedId(el.id);
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    setDragOffset({
      x: (e.clientX - rect.left) * scaleX - el.x,
      y: (e.clientY - rect.top) * scaleY - el.y
    });
    setDragging(el.id);
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const newX = (e.clientX - rect.left) * scaleX - dragOffset.x;
    const newY = (e.clientY - rect.top) * scaleY - dragOffset.y;
    updateElement(dragging, { x: Math.max(0, Math.min(CANVAS_W, newX)), y: Math.max(0, Math.min(CANVAS_H, newY)) });
  }, [dragging, dragOffset, updateElement]);

  const handleMouseUp = useCallback(() => { setDragging(null); }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleDoubleClick = (e, el) => {
    if (el.type === "text") { e.stopPropagation(); setEditingText(el.id); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/invitaciones/${invitacionId}/diseno`, { diseno_data: { elements, bgColor } });
      toast.success("Diseño guardado");
    } catch (error) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    setSelectedId(null);
    setEditingText(null);
    await new Promise(r => setTimeout(r, 100));
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(canvasRef.current, {
        scale: 2, useCORS: true, backgroundColor: bgColor,
        width: canvasRef.current.offsetWidth, height: canvasRef.current.offsetHeight
      });
      const link = document.createElement("a");
      link.download = `invitacion_${invitacionId}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Invitación exportada como PNG");
    } catch (error) { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  const renderElement = (el) => {
    const isSelected = selectedId === el.id;
    const isEditing = editingText === el.id;

    if (el.type === "text") {
      return (
        <div key={el.id} data-testid={`canvas-el-${el.id}`}
          onMouseDown={(e) => handleMouseDown(e, el)}
          onDoubleClick={(e) => handleDoubleClick(e, el)}
          onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
          style={{
            position: "absolute", left: el.x - (el.width || 400) / 2, top: el.y - el.fontSize / 2,
            width: el.width || 400, fontFamily: `"${el.fontFamily}", serif`, fontSize: el.fontSize,
            fontWeight: el.bold ? "bold" : "normal", fontStyle: el.italic ? "italic" : "normal",
            textDecoration: el.underline ? "underline" : "none", color: el.color,
            textAlign: el.align || "center", cursor: isEditing ? "text" : "move",
            outline: isSelected ? "2px dashed #3b82f6" : "none", outlineOffset: 4,
            padding: "4px 8px", lineHeight: 1.4, whiteSpace: "pre-wrap",
            userSelect: isEditing ? "text" : "none", wordBreak: "break-word",
          }}
        >
          {isEditing ? (
            <textarea autoFocus value={el.text}
              onChange={(e) => updateElement(el.id, { text: e.target.value })}
              onBlur={() => setEditingText(null)}
              onKeyDown={(e) => { if (e.key === "Escape") setEditingText(null); }}
              style={{
                width: "100%", background: "transparent", border: "none", outline: "none",
                fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit",
                fontStyle: "inherit", color: "inherit", textAlign: "inherit",
                resize: "none", lineHeight: "inherit",
              }}
              rows={el.text.split("\n").length || 1}
            />
          ) : el.text}
        </div>
      );
    }
    if (el.type === "rect" || el.type === "circle") {
      return (
        <div key={el.id} data-testid={`canvas-el-${el.id}`}
          onMouseDown={(e) => handleMouseDown(e, el)}
          onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
          style={{
            position: "absolute", left: el.x, top: el.y, width: el.width, height: el.height,
            backgroundColor: el.color, borderRadius: el.type === "circle" ? "50%" : el.borderRadius || 0,
            cursor: "move", outline: isSelected ? "2px dashed #3b82f6" : "none", outlineOffset: 4,
          }}
        />
      );
    }
    if (el.type === "line") {
      return (
        <div key={el.id} data-testid={`canvas-el-${el.id}`}
          onMouseDown={(e) => handleMouseDown(e, el)}
          onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
          style={{
            position: "absolute", left: el.x, top: el.y, width: el.width, height: 2,
            backgroundColor: el.color, cursor: "move",
            outline: isSelected ? "2px dashed #3b82f6" : "none", outlineOffset: 4,
          }}
        />
      );
    }
    return null;
  };

  // === LOADING / ERROR STATE ===
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-500 text-sm">Cargando diseñador...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center space-y-4">
          <p className="text-red-600">{loadError}</p>
          <Button variant="outline" onClick={() => navigate("/panel/invitaciones")} data-testid="btn-volver-error">
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Invitaciones
          </Button>
        </div>
      </div>
    );
  }

  // === TEMPLATE SELECTION SCREEN ===
  const templateTypes = [...new Set(TEMPLATES.map(t => t.tipo))];
  const filteredTemplates = templateFilter === "todos" ? TEMPLATES : TEMPLATES.filter(t => t.tipo === templateFilter);

  if (showTemplates) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/panel/invitaciones")} data-testid="btn-volver-invitaciones">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="font-display text-2xl text-stone-900">Diseñar Invitación</h1>
            {cliente && <p className="text-stone-500 text-sm">{cliente.nombre} - {cliente.tipo_evento} - {cliente.nombre_festejado}</p>}
          </div>
        </div>

        <div className="text-center py-4">
          <h2 className="font-display text-xl text-stone-800 mb-2">Elige una plantilla</h2>
          <p className="text-stone-500 mb-4">Cada plantilla incluye ubicación, RSVP, mesa de regalos y más</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button onClick={() => setTemplateFilter("todos")}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${templateFilter === "todos" ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
              Todos
            </button>
            {templateTypes.map(t => (
              <button key={t} onClick={() => setTemplateFilter(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${templateFilter === t ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                data-testid={`filter-template-${t}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredTemplates.map(template => (
            <button key={template.id} onClick={() => applyTemplate(template)}
              className="group text-left border border-stone-200 rounded-lg overflow-hidden hover:shadow-xl transition-all hover:border-emerald-400"
              data-testid={`template-${template.id}`}>
              <div className="aspect-[2/3] relative" style={{ backgroundColor: template.bgColor }}>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-2 overflow-hidden">
                  {template.elements.length > 0 ? (
                    <div className="text-center" style={{ transform: "scale(0.25)", transformOrigin: "center", width: CANVAS_W }}>
                      {template.elements.filter(el => el.type === "text").slice(0, 4).map(el => (
                        <p key={el.id} style={{ fontFamily: `"${el.fontFamily}", serif`, color: el.color, fontSize: el.fontSize, fontWeight: el.bold ? "bold" : "normal", lineHeight: 1.3, margin: "4px 0" }}>
                          {el.text.split("\n")[0]}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <Plus className="w-8 h-8 text-stone-300" />
                  )}
                </div>
              </div>
              <div className="p-3 bg-white">
                <p className="font-medium text-stone-900 text-sm">{template.nombre}</p>
                <p className="text-xs text-stone-500">{template.tipo} &middot; {template.elements.filter(e => e.type === "text").length} secciones</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // === EDITOR SCREEN ===
  return (
    <div className="flex flex-col h-[calc(100vh-80px)]">
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-stone-200">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/panel/invitaciones")} data-testid="btn-volver-designer">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="h-6 w-px bg-stone-200" />
          <span className="text-sm text-stone-600">{cliente?.nombre} - {cliente?.tipo_evento}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowTemplates(true)} className="border-stone-300 text-xs" data-testid="btn-templates">
            <Layers className="w-4 h-4 mr-1" /> Plantillas
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="border-stone-300 text-xs" data-testid="btn-guardar-diseno">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando..." : "Guardar"}
          </Button>
          <Button size="sm" onClick={handleExport} disabled={exporting} className="btn-primary text-xs" data-testid="btn-exportar-png">
            <Download className="w-4 h-4 mr-1" /> {exporting ? "Exportando..." : "Exportar PNG"}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel */}
        <div className="w-60 bg-white border-r border-stone-200 p-3 space-y-3 overflow-y-auto">
          {/* Basic Elements */}
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Elementos</p>
            <div className="grid grid-cols-2 gap-1">
              <Button variant="outline" size="sm" onClick={addText} className="justify-start border-stone-300 text-xs h-8" data-testid="btn-add-text">
                <Type className="w-3 h-3 mr-1" /> Texto
              </Button>
              <Button variant="outline" size="sm" onClick={() => addShape("rect")} className="justify-start border-stone-300 text-xs h-8">
                <Square className="w-3 h-3 mr-1" /> Rect
              </Button>
              <Button variant="outline" size="sm" onClick={() => addShape("circle")} className="justify-start border-stone-300 text-xs h-8">
                <Circle className="w-3 h-3 mr-1" /> Círculo
              </Button>
              <Button variant="outline" size="sm" onClick={() => addShape("line")} className="justify-start border-stone-300 text-xs h-8">
                <Minus className="w-3 h-3 mr-1" /> Línea
              </Button>
            </div>
          </div>

          {/* QUICK INFO BLOCKS */}
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Insertar Sección</p>
            <div className="space-y-1">
              {INFO_BLOCKS.map(block => (
                <button key={block.label} onClick={() => addInfoBlock(block)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-stone-700 hover:bg-emerald-50 hover:text-emerald-800 rounded transition-colors text-left"
                  data-testid={`btn-info-${block.label.toLowerCase().replace(/ /g, '-')}`}>
                  <block.icon className="w-3.5 h-3.5 flex-shrink-0 text-stone-400" />
                  {block.label}
                </button>
              ))}
            </div>
          </div>

          {/* Background */}
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Fondo</p>
            <div className="flex items-center gap-2">
              <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0" data-testid="input-bg-color" />
              <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="border-stone-300 text-xs h-7" />
            </div>
          </div>

          {/* Layers */}
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Capas ({elements.length})</p>
            <div className="space-y-0.5 max-h-36 overflow-y-auto">
              {elements.map((el, idx) => (
                <button key={el.id} onClick={() => setSelectedId(el.id)}
                  className={`w-full text-left px-2 py-1 rounded text-xs transition-colors truncate ${selectedId === el.id ? 'bg-blue-100 text-blue-800' : 'hover:bg-stone-100 text-stone-600'}`}>
                  {el.type === "text" ? el.text.substring(0, 25) : el.type} #{idx + 1}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 bg-stone-100 flex items-center justify-center p-6 overflow-auto" onClick={() => { setSelectedId(null); setEditingText(null); }}>
          <div ref={canvasRef} data-testid="design-canvas"
            style={{
              width: CANVAS_W, height: CANVAS_H, backgroundColor: bgColor,
              position: "relative", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              overflow: "hidden", flexShrink: 0,
            }}
            onClick={(e) => { e.stopPropagation(); setSelectedId(null); setEditingText(null); }}>
            {elements.map(renderElement)}
          </div>
        </div>

        {/* Right Panel - Properties */}
        <div className="w-64 bg-white border-l border-stone-200 p-4 space-y-3 overflow-y-auto">
          {selectedElement ? (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-stone-500 uppercase tracking-wider">Propiedades</p>
                <Button variant="ghost" size="sm" onClick={() => deleteElement(selectedId)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0" data-testid="btn-delete-element">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {selectedElement.type === "text" && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Texto</Label>
                    <Textarea value={selectedElement.text} onChange={(e) => updateElement(selectedId, { text: e.target.value })} rows={4} className="border-stone-300 text-xs" data-testid="prop-text" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fuente</Label>
                    <Select value={selectedElement.fontFamily} onValueChange={(v) => updateElement(selectedId, { fontFamily: v })}>
                      <SelectTrigger className="border-stone-300 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FONTS.map(f => (<SelectItem key={f} value={f}><span style={{ fontFamily: f }}>{f}</span></SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tamaño</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { fontSize: Math.max(8, selectedElement.fontSize - 2) })}><Minus className="w-3 h-3" /></Button>
                      <Input type="number" value={selectedElement.fontSize} onChange={(e) => updateElement(selectedId, { fontSize: parseInt(e.target.value) || 16 })} className="border-stone-300 h-7 text-xs text-center" data-testid="prop-font-size" />
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { fontSize: selectedElement.fontSize + 2 })}><Plus className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Color</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={selectedElement.color} onChange={(e) => updateElement(selectedId, { color: e.target.value })} className="w-7 h-7 rounded cursor-pointer border-0" />
                      <Input value={selectedElement.color} onChange={(e) => updateElement(selectedId, { color: e.target.value })} className="border-stone-300 text-xs h-7" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Estilo</Label>
                    <div className="flex gap-1">
                      <Button variant={selectedElement.bold ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { bold: !selectedElement.bold })}><Bold className="w-3 h-3" /></Button>
                      <Button variant={selectedElement.italic ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { italic: !selectedElement.italic })}><Italic className="w-3 h-3" /></Button>
                      <Button variant={selectedElement.underline ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { underline: !selectedElement.underline })}><Underline className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Alineación</Label>
                    <div className="flex gap-1">
                      <Button variant={selectedElement.align === "left" ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { align: "left" })}><AlignLeft className="w-3 h-3" /></Button>
                      <Button variant={selectedElement.align === "center" ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { align: "center" })}><AlignCenter className="w-3 h-3" /></Button>
                      <Button variant={selectedElement.align === "right" ? "default" : "outline"} size="sm" className="h-7 w-7 p-0" onClick={() => updateElement(selectedId, { align: "right" })}><AlignRight className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ancho</Label>
                    <Input type="number" value={selectedElement.width || 400} onChange={(e) => updateElement(selectedId, { width: parseInt(e.target.value) || 400 })} className="border-stone-300 h-7 text-xs" />
                  </div>
                </div>
              )}

              {(selectedElement.type === "rect" || selectedElement.type === "circle" || selectedElement.type === "line") && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Color</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={selectedElement.color} onChange={(e) => updateElement(selectedId, { color: e.target.value })} className="w-7 h-7 rounded cursor-pointer border-0" />
                      <Input value={selectedElement.color} onChange={(e) => updateElement(selectedId, { color: e.target.value })} className="border-stone-300 text-xs h-7" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Ancho</Label>
                      <Input type="number" value={selectedElement.width} onChange={(e) => updateElement(selectedId, { width: parseInt(e.target.value) || 100 })} className="border-stone-300 h-7 text-xs" />
                    </div>
                    {selectedElement.type !== "line" && (
                      <div className="space-y-1">
                        <Label className="text-xs">Alto</Label>
                        <Input type="number" value={selectedElement.height} onChange={(e) => updateElement(selectedId, { height: parseInt(e.target.value) || 100 })} className="border-stone-300 h-7 text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs">Posición</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-stone-400">X</Label>
                    <Input type="number" value={Math.round(selectedElement.x)} onChange={(e) => updateElement(selectedId, { x: parseInt(e.target.value) || 0 })} className="border-stone-300 h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-stone-400">Y</Label>
                    <Input type="number" value={Math.round(selectedElement.y)} onChange={(e) => updateElement(selectedId, { y: parseInt(e.target.value) || 0 })} className="border-stone-300 h-7 text-xs" />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Move className="w-8 h-8 text-stone-300 mx-auto mb-2" />
              <p className="text-sm text-stone-500">Selecciona un elemento para editar</p>
              <p className="text-xs text-stone-400 mt-1">Doble clic en texto para editar inline</p>
            </div>
          )}

          {/* Client Info */}
          {cliente && (
            <div className="border-t border-stone-200 pt-3 mt-3">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Info del Cliente</p>
              <div className="space-y-1 text-xs text-stone-600">
                <p><strong>Evento:</strong> {cliente.tipo_evento}</p>
                <p><strong>Festejado:</strong> {cliente.nombre_festejado}</p>
                <p><strong>Fecha:</strong> {cliente.fecha_evento}</p>
                {cliente.tema && <p><strong>Tema:</strong> {cliente.tema}</p>}
                {cliente.colorimetria && <p><strong>Colores:</strong> {cliente.colorimetria}</p>}
                {cliente.ubicacion && <p><strong>Lugar:</strong> {cliente.ubicacion}</p>}
                {cliente.codigo_vestimenta && <p><strong>Vestimenta:</strong> {cliente.codigo_vestimenta}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitacionDesigner;
